import React from 'react';
import { Link } from 'react-router-dom';
import { featuredProjects, pillars, Project } from '../data/projectsData';

const ProjectsTeaser: React.FC = () => {
  return (
    <section id="projets" className="projects-showcase">
      {/* Decorative background */}
      <div className="ps-bg-blue" />
      <div className="ps-bg-gold" />

      <div className="ps-inner">
        {/* Header */}
        <div className="reveal ps-header">
          <div className="sec-tag">Réalisations</div>
          <h2 className="sec-title">
            Nos <em>Projets</em> récents
          </h2>
          <div className="gold-bar" />
          <p className="ps-intro">
            Dashboards temps réel, intégrations CRM, automatisation, IA générative, plateformes web,
            e-gouvernement, cloud et cybersécurité. Voici trois projets représentatifs.
          </p>

          {/* Attractive portfolio CTA */}
          <Link to="/projets" className="ps-portfolio-cta">
            <span className="ps-portfolio-count">13</span>
            <span className="ps-portfolio-body">
              <span className="ps-portfolio-main">Explorer tout le portfolio</span>
              <span className="ps-portfolio-sub">13 projets · 8 domaines d'expertise</span>
            </span>
            <span className="ps-portfolio-arrow" aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                <path d="M4 10h12M11 4l6 6-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          </Link>
        </div>

        {/* 4 Pillars */}
        <div className="reveal d1 ps-pillars">
          {pillars.map((p) => (
            <div key={p.title} className="ps-pillar">
              <div className="ps-pillar-icon" style={{ background: `${p.color}14`, color: p.color }}>
                {p.icon}
              </div>
              <div>
                <h3 className="ps-pillar-title">{p.title}</h3>
                <p className="ps-pillar-desc">{p.desc}</p>
              </div>
              <div className="ps-pillar-bullets">
                {p.bullets.map((b) => (
                  <div key={b} className="ps-pillar-bullet">
                    <div className="ps-pillar-dot" style={{ background: p.color }} />
                    <span>{b}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Teaser grid — 3 featured */}
        <div className="ps-grid">
          {featuredProjects.map((p, i) => (
            <ProjectCard key={p.id} project={p} index={i} />
          ))}
        </div>

        {/* CTA → projets page */}
        <div className="reveal d3 ps-cta">
          <div>
            <div className="ps-cta-title">Explorer tous les projets</div>
            <div className="ps-cta-sub">9 réalisations dans 4 domaines d'expertise — dashboards, CRM, automatisation, IA.</div>
          </div>
          <Link to="/projets" className="btn-primary">
            Voir tous les projets
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M1 7h12M8 2l5 5-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </Link>
        </div>
      </div>

      <style>{teaserStyles}</style>
    </section>
  );
};

const ProjectCard: React.FC<{ project: Project; index: number }> = ({ project, index }) => {
  const delay = Math.min(index, 4);
  return (
    <article className={`reveal d${delay} ps-card span-${project.span}`}>
      <div className="ps-card-mockup">{project.mockup}</div>
      <div>
        <div className="ps-card-tags">
          {project.tags.map((t) => (
            <span key={t} className="ps-tag" style={{ background: `${project.accent}14`, color: project.accent }}>
              <span className="ps-tag-dot" style={{ background: project.accent }} />
              {t}
            </span>
          ))}
        </div>
        <div className="ps-card-head">
          <div>
            <div className="ps-card-num">— {project.number}</div>
            <h3 className="ps-card-title">{project.title}</h3>
          </div>
          <div className="ps-arrow" style={{ color: project.accent }}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M4 10h12M11 4l6 6-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </div>
        <p className="ps-card-desc">{project.description}</p>
        <div className="ps-stack">
          {project.stack.map((s) => (
            <span key={s} className="ps-stack-item">{s}</span>
          ))}
        </div>
      </div>
    </article>
  );
};

/* Shared styles (same as ProjectsShowcase) */
const teaserStyles = `
  .projects-showcase {
    padding: 6rem 0;
    background: var(--cream2);
    position: relative;
    overflow: hidden;
  }
  .ps-bg-blue {
    position: absolute; top: -100px; right: -100px;
    width: 400px; height: 400px;
    background: radial-gradient(circle, rgba(26,111,168,.08), transparent 70%);
    pointer-events: none;
  }
  .ps-bg-gold {
    position: absolute; bottom: -100px; left: -100px;
    width: 360px; height: 360px;
    background: radial-gradient(circle, rgba(184,146,42,.08), transparent 70%);
    pointer-events: none;
  }
  .ps-inner {
    max-width: 1200px; margin: 0 auto; padding: 0 2rem; position: relative;
  }
  .ps-header { margin-bottom: 3rem; max-width: 720px; }
  .ps-intro {
    font-family: 'Montserrat', sans-serif;
    font-size: .88rem; font-weight: 300;
    color: var(--ink2); line-height: 1.8;
  }
  .ps-intro-link {
    color: var(--blue); font-weight: 500;
    text-decoration: none; border-bottom: 1px solid transparent;
    transition: border-color .2s;
  }
  .ps-intro-link:hover { border-color: var(--blue); }

  /* Portfolio CTA — attractive click magnet */
  .ps-portfolio-cta {
    display: inline-flex;
    align-items: center;
    gap: 1.2rem;
    margin-top: 1.8rem;
    padding: 1rem 1.2rem 1rem 1.5rem;
    background: var(--white);
    border: 1px solid var(--border);
    text-decoration: none;
    position: relative;
    overflow: hidden;
    transition: transform .35s cubic-bezier(.4, 0, .2, 1), box-shadow .35s ease, border-color .35s ease;
    box-shadow: 0 4px 16px -6px rgba(26,111,168,.12);
  }
  .ps-portfolio-cta::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: linear-gradient(180deg, var(--blue), var(--gold));
  }
  .ps-portfolio-cta::after {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(184,146,42,.12), transparent);
    transition: left .6s ease;
  }
  .ps-portfolio-cta:hover {
    transform: translateX(4px);
    border-color: var(--gold);
    box-shadow: 0 12px 30px -10px rgba(184,146,42,.25);
  }
  .ps-portfolio-cta:hover::after { left: 100%; }
  .ps-portfolio-cta:hover .ps-portfolio-arrow { transform: translateX(5px); color: var(--gold); }
  .ps-portfolio-cta:hover .ps-portfolio-count {
    background-position: 100% 50%;
  }

  .ps-portfolio-count {
    font-family: 'Cormorant Garamond', Georgia, serif;
    font-size: 3rem;
    font-weight: 600;
    font-style: italic;
    line-height: 1;
    background: linear-gradient(90deg, var(--blue) 0%, var(--gold) 50%, var(--blue) 100%);
    background-size: 200% 100%;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    transition: background-position .8s ease;
    flex-shrink: 0;
  }
  .ps-portfolio-body {
    display: flex;
    flex-direction: column;
    gap: .15rem;
    min-width: 0;
  }
  .ps-portfolio-main {
    font-family: 'Cormorant Garamond', Georgia, serif;
    font-size: 1.15rem;
    font-weight: 600;
    color: var(--ink);
    line-height: 1.2;
  }
  .ps-portfolio-sub {
    font-family: 'Montserrat', sans-serif;
    font-size: .62rem;
    font-weight: 500;
    letter-spacing: .08em;
    color: var(--ink2);
    text-transform: uppercase;
  }
  .ps-portfolio-arrow {
    color: var(--blue);
    transition: transform .35s cubic-bezier(.4, 0, .2, 1), color .25s ease;
    margin-left: auto;
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid var(--border);
    border-radius: 50%;
  }
  .ps-portfolio-cta:hover .ps-portfolio-arrow {
    border-color: var(--gold);
    background: rgba(184,146,42,.08);
  }

  @media (max-width: 520px) {
    .ps-portfolio-cta {
      gap: .8rem;
      padding: .8rem 1rem .8rem 1.2rem;
      width: 100%;
    }
    .ps-portfolio-count { font-size: 2.2rem; }
    .ps-portfolio-main { font-size: 1rem; }
    .ps-portfolio-sub { font-size: .55rem; letter-spacing: .04em; }
    .ps-portfolio-arrow { width: 32px; height: 32px; }
  }

  .ps-pillars {
    display: grid; grid-template-columns: repeat(4, 1fr);
    gap: 1px; background: var(--border);
    margin-bottom: 3.5rem; border: 1px solid var(--border);
  }
  .ps-pillar {
    background: var(--white); padding: 1.5rem 1.3rem;
    display: flex; flex-direction: column; gap: .8rem;
  }
  .ps-pillar-icon {
    width: 40px; height: 40px;
    display: flex; align-items: center; justify-content: center;
    border-radius: 4px;
  }
  .ps-pillar-title {
    font-family: 'Cormorant Garamond', Georgia, serif;
    font-size: 1.25rem; font-weight: 600;
    color: var(--ink); margin: 0 0 .4rem;
  }
  .ps-pillar-desc {
    font-family: 'Montserrat', sans-serif;
    font-size: .75rem; font-weight: 300;
    color: var(--ink2); line-height: 1.65; margin: 0;
  }
  .ps-pillar-bullets { display: flex; flex-direction: column; gap: .3rem; margin-top: .3rem; }
  .ps-pillar-bullet {
    display: flex; align-items: center; gap: .5rem;
    font-family: 'Montserrat', sans-serif; font-size: .68rem; color: var(--ink);
  }
  .ps-pillar-dot { width: 4px; height: 4px; border-radius: 50%; flex-shrink: 0; }

  .ps-grid {
    display: grid; grid-template-columns: repeat(12, 1fr); gap: 1.5rem;
  }
  .ps-card {
    background: var(--white); border: 1px solid var(--border);
    padding: 1.5rem; display: flex; flex-direction: column; gap: 1.2rem;
    transition: transform .35s ease, box-shadow .35s ease, border-color .35s ease;
    min-width: 0; overflow: hidden;
  }
  .ps-card > * { min-width: 0; }
  .ps-card.span-large { grid-column: span 12; }
  .ps-card.span-standard { grid-column: span 6; }
  .ps-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 20px 50px -15px rgba(26,111,168,.2);
    border-color: var(--blue);
  }
  .ps-card:hover .ps-arrow { transform: translateX(4px); opacity: 1; }
  .ps-card-mockup {
    position: relative; width: 100%; min-width: 0;
    overflow: hidden; border-radius: 8px;
  }
  .ps-card-mockup > * { max-width: 100%; }

  .ps-cta {
    margin-top: 4rem; padding: 2.5rem 2rem;
    background: var(--white); border: 1px solid var(--border);
    display: flex; justify-content: space-between; align-items: center;
    flex-wrap: wrap; gap: 1.5rem;
  }
  .ps-cta-title {
    font-family: 'Cormorant Garamond', Georgia, serif;
    font-size: 1.6rem; font-weight: 600;
    color: var(--ink); margin-bottom: .3rem;
  }
  .ps-cta-sub {
    font-family: 'Montserrat', sans-serif;
    font-size: .8rem; color: var(--ink2);
  }

  @media (max-width: 1024px) {
    .ps-pillars { grid-template-columns: repeat(2, 1fr); }
  }
  @media (max-width: 768px) {
    .projects-showcase { padding: 4rem 0; }
    .ps-inner { padding: 0 1rem; }
    .ps-grid { gap: 1rem; grid-template-columns: 1fr; }
    .ps-card.span-large,
    .ps-card.span-standard { grid-column: 1 / -1; }
    .ps-card { padding: .9rem; gap: 1rem; }
    .ps-card-title { font-size: 1.15rem; }
    .ps-card-desc { font-size: .7rem; }
    .ps-cta { padding: 1.5rem 1.1rem; }
    .ps-cta-title { font-size: 1.25rem; }
    .ps-cta-sub { font-size: .72rem; }
  }
  @media (max-width: 600px) {
    .ps-pillars { grid-template-columns: 1fr; }
    .ps-pillar { padding: 1.2rem 1rem; }
  }

  .ps-card-tags { display: flex; gap: .4rem; flex-wrap: wrap; margin-bottom: .7rem; }
  .ps-tag {
    display: inline-flex; align-items: center; gap: .3rem;
    padding: .2rem .55rem;
    font-family: 'Montserrat', sans-serif;
    font-size: .55rem; font-weight: 600;
    letter-spacing: .1em; text-transform: uppercase;
    border-radius: 2px;
  }
  .ps-tag-dot { width: 4px; height: 4px; border-radius: 50%; }
  .ps-card-head {
    display: flex; justify-content: space-between; align-items: flex-start;
    gap: 1rem; margin-bottom: .5rem;
  }
  .ps-card-num {
    font-family: 'Montserrat', sans-serif;
    font-size: .6rem; font-weight: 600;
    color: var(--ink2); letter-spacing: .2em; margin-bottom: .3rem;
  }
  .ps-card-title {
    font-family: 'Cormorant Garamond', Georgia, serif;
    font-size: 1.4rem; font-weight: 600;
    color: var(--ink); margin: 0; line-height: 1.2;
  }
  .ps-arrow {
    opacity: .5;
    transition: transform .3s ease, opacity .3s ease;
    flex-shrink: 0; margin-top: .3rem;
  }
  .ps-card-desc {
    font-family: 'Montserrat', sans-serif;
    font-size: .78rem; font-weight: 300;
    color: var(--ink2); line-height: 1.7; margin: 0 0 1rem;
  }
  .ps-stack {
    display: flex; gap: .4rem; flex-wrap: wrap;
    padding-top: .9rem; border-top: 1px solid var(--border);
  }
  .ps-stack-item {
    font-family: 'Montserrat', sans-serif;
    font-size: .58rem; font-weight: 500;
    color: var(--ink2); padding: .2rem .5rem;
    border: 1px solid var(--border); border-radius: 2px;
  }

  @keyframes livePulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: .4; transform: scale(1.4); }
  }
  @keyframes caret { 50% { opacity: 0; } }
`;

export default ProjectsTeaser;
